import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthService } from 'src/app/shared/services/auth.service';
import { PortfolioService } from '../portfolio/portfolio.service';
import { ExperienceAltiService } from './services/experience-alti.service';
import { ExperienceThemeService, ExperienceTheme } from './services/experience-theme.service';
import { HomeMiniChartComponent, HomeMiniKind, HomeMiniWeight } from './components/home-mini-chart.component';
import { PdfDropComponent } from './components/pdf-drop/pdf-drop.component';

const INITIAL_PORTFOLIOS_ON_HOME = 5;
const LOAD_MORE_BATCH_SIZE = 5;

type HeadlineTone = 'pos' | 'neg' | 'warn' | 'info';
type HeadlineDir = 'up' | 'down';

interface Headline {
  kind: HomeMiniKind;
  dir: HeadlineDir;
  label: string;
  level: number;
  tone: HeadlineTone;
}

interface PortfolioFeedItem {
  id: number;
  name: string;
  when: string;
  headline: Headline;
  summary: string;
  series: number[];
  weights: HomeMiniWeight[];
  caption: string;
  risk: number;
  holdings: number;
  topName: string;
  metricText: string;
  arrow: '▲' | '▼';
  chartColor: string;
  isLoadingSeries: boolean;
}

@Component({
  selector: 'app-home-experience',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [CommonModule, FormsModule, HomeMiniChartComponent, PdfDropComponent],
  templateUrl: './home-experience.component.html',
})
export class HomeExperienceComponent implements OnInit {
  theme: ExperienceTheme = 'dark';
  userName = '';
  greeting = 'Good morning';

  feed: PortfolioFeedItem[] = [];
  feedTotal = 0;
  isLoading = true;
  isLoadingMore = false;
  hasError = false;

  sortedPortfolios: any[] = [];

  askValue = '';

  pdfDropOpen = false;

  suggestionsMain = [
    { q: 'Detect risk hot spots', emoji: '🔥', label: 'Detect risk hot spots' },
    { q: 'Suggest hedges', emoji: '🛡️', label: 'Suggest hedges' },
    { q: 'Which portfolios need rebalancing?', emoji: '🔄', label: 'Rebalance' },
    { q: 'Summarise changes across all portfolios', emoji: '📊', label: 'Summarise' },
  ];

  suggestionsActions: Array<{ q: string; emoji: string; label: string; action?: 'upload' }> = [
    { q: 'Upload research PDF', emoji: '📄', label: 'Upload PDF', action: 'upload' },
    { q: 'Optimise all of your portfolios', emoji: '✨', label: 'Optimise all' },
  ];

  private readonly CHART_COLOR: Record<HeadlineTone, string> = {
    pos: '#00E5A0',
    neg: '#FF5C7A',
    warn: '#FFB547',
    info: '#54C7FF',
  };

  private readonly CAPTIONS: Record<HomeMiniKind, string> = {
    return: 'Cumulative return',
    risk: 'Realised volatility (rolling)',
    drawdown: 'Drawdown curve',
    concentration: 'Top holdings',
  };

  constructor(
    private auth: AuthService,
    private portfolioService: PortfolioService,
    private router: Router,
    private themeService: ExperienceThemeService,
    private altiService: ExperienceAltiService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.theme = this.themeService.current;
    this.themeService.current$.subscribe((t) => (this.theme = t));
    this.greeting = this.computeGreeting();

    this.auth.getCurrentUser().subscribe((user: any) => {
      this.userName = this.resolveUserName(user);
    });

    this.fetchPortfolios();
  }

  toggleTheme(): void {
    this.themeService.toggle();
  }

  exitExperience(): void {
    this.router.navigateByUrl('/app');
  }

  goToAllPortfolios(): void {
    this.router.navigateByUrl('/app/home-experience/all');
  }

  goToReports(): void {
    this.router.navigateByUrl('/app/home-experience/reports');
  }

  trackByFeed(_i: number, p: PortfolioFeedItem): number {
    return p.id;
  }

  onCardClick(p: PortfolioFeedItem): void {
    const navigate = () =>
      this.router.navigate(['/app/home-experience/portfolio', p.id, 'backtest']);

    const anyDoc = document as any;
    if (typeof anyDoc.startViewTransition === 'function') {
      try {
        anyDoc.startViewTransition(() => navigate());
      } catch {
        navigate();
      }
    } else {
      navigate();
    }
  }

  askAlti(q: string): void {
    const trimmed = (q || '').trim();
    if (!trimmed) return;
    this.altiService.ask('list', trimmed);
  }

  submitAsk(): void {
    const val = this.askValue;
    this.askValue = '';
    this.askAlti(val);
  }

  onSuggestClick(s: { q: string; action?: 'upload' }): void {
    if (s.action === 'upload') {
      this.openUpload();
      return;
    }
    this.askAlti(s.q);
  }

  openUpload(): void {
    this.pdfDropOpen = true;
  }

  closeUpload(): void {
    this.pdfDropOpen = false;
  }

  onUploaded(filename: string): void {
    this.pdfDropOpen = false;
    try {
      sessionStorage.setItem('cimalti-just-uploaded', filename);
    } catch {
      /* noop */
    }
    setTimeout(() => {
      this.router.navigate(['/app/home-experience/reports'], {
        queryParams: { new: 1 },
      });
    }, 500);
  }

  private fetchPortfolios(): void {
    this.isLoading = true;
    this.hasError = false;

    this.portfolioService.getItems().subscribe({
      next: (resp: any) => {
        if (!resp || resp.error) {
          this.isLoading = false;
          this.hasError = true;
          this.feed = [];
          this.sortedPortfolios = [];
          return;
        }
        const merged = this.mergeLists(resp);
        this.sortedPortfolios = this.sortByRecency(merged);
        this.feedTotal = this.sortedPortfolios.length;
        const initial = this.sortedPortfolios.slice(0, INITIAL_PORTFOLIOS_ON_HOME);
        this.feed = initial.map((p) => this.buildFeedItem(p));
        this.isLoading = false;
        this.hydrateSeries(initial, 0);
      },
      error: () => {
        this.isLoading = false;
        this.hasError = true;
        this.feed = [];
        this.sortedPortfolios = [];
      },
    });
  }

  get hasMorePortfolios(): boolean {
    return this.sortedPortfolios.length > this.feed.length;
  }

  get remainingPortfolioCount(): number {
    return Math.max(this.sortedPortfolios.length - this.feed.length, 0);
  }

  onLoadMore(): void {
    if (this.isLoadingMore || !this.hasMorePortfolios) return;
    this.isLoadingMore = true;

    const startIndex = this.feed.length;
    const nextSlice = this.sortedPortfolios.slice(
      startIndex,
      startIndex + LOAD_MORE_BATCH_SIZE,
    );
    const newItems = nextSlice.map((p) => this.buildFeedItem(p));
    this.feed = [...this.feed, ...newItems];

    this.hydrateSeries(nextSlice, startIndex, () => {
      this.isLoadingMore = false;
      this.cdr.markForCheck();
    });
  }

  private hydrateSeries(rawList: any[], offset: number, done?: () => void): void {
    if (!rawList.length) {
      if (done) done();
      return;
    }
    const requests = rawList.map((raw) =>
      this.portfolioService.fetchPerformancePlot(raw.id).pipe(catchError(() => of(null))),
    );
    forkJoin(requests).subscribe((results) => {
      results.forEach((plot, i) => {
        const item = this.feed[offset + i];
        if (!item) return;
        this.populateSeries(item, plot);
        item.isLoadingSeries = false;
      });
      this.feed = [...this.feed];
      this.cdr.markForCheck();
      if (done) done();
    });
  }

  private populateSeries(item: PortfolioFeedItem, plot: any): void {
    const cum = plot?.cumulative_returns?.Portfolio;
    if (!Array.isArray(cum) || cum.length < 2) return;
    const values: number[] = [];
    const timestamps: number[] = [];
    for (const entry of cum) {
      if (!Array.isArray(entry)) continue;
      if (typeof entry[1] !== 'number' || !isFinite(entry[1])) continue;
      values.push(entry[1]);
      const ts = Date.parse(entry[0]);
      timestamps.push(isFinite(ts) ? ts : NaN);
    }
    if (values.length < 2) return;

    if (item.headline.kind === 'return') {
      const base = values[0] || 1;
      const indexed = values.map((v) => (v / base) * 100);
      item.series = this.sampleSeries(indexed, 40);
      return;
    }

    if (item.headline.kind === 'drawdown') {
      const dd: number[] = [];
      let peak = values[0];
      for (const v of values) {
        if (v > peak) peak = v;
        dd.push(((v - peak) / peak) * 100);
      }
      item.series = this.sampleSeries(dd, 40);
      return;
    }

    if (item.headline.kind === 'risk') {
      item.series = this.computeRollingVol(values, timestamps);
      return;
    }
  }

  private computeRollingVol(values: number[], timestamps: number[]): number[] {
    const returns: number[] = [];
    for (let i = 1; i < values.length; i++) {
      const prev = values[i - 1];
      if (!prev || !isFinite(prev)) continue;
      returns.push((values[i] - prev) / prev);
    }
    if (returns.length < 4) return [];

    const annualization = this.detectAnnualizationFactor(timestamps);
    const window = Math.max(3, Math.min(12, Math.floor(returns.length / 3)));
    const out: number[] = [];
    for (let i = window; i <= returns.length; i++) {
      const slice = returns.slice(i - window, i);
      const mean = slice.reduce((a, b) => a + b, 0) / slice.length;
      let sumSq = 0;
      for (const v of slice) sumSq += (v - mean) * (v - mean);
      const variance = sumSq / Math.max(1, slice.length - 1);
      const sd = Math.sqrt(variance) * annualization * 100;
      out.push(sd);
    }
    return this.sampleSeries(out, 12);
  }

  private detectAnnualizationFactor(timestamps: number[]): number {
    const valid = timestamps.filter((t) => isFinite(t));
    if (valid.length < 3) return Math.sqrt(12);
    const gaps: number[] = [];
    for (let i = 1; i < valid.length && gaps.length < 12; i++) {
      const gap = (valid[i] - valid[i - 1]) / 86_400_000;
      if (gap > 0) gaps.push(gap);
    }
    if (!gaps.length) return Math.sqrt(12);
    const avgDays = gaps.reduce((a, b) => a + b, 0) / gaps.length;
    if (avgDays <= 2) return Math.sqrt(252);
    if (avgDays <= 10) return Math.sqrt(52);
    return Math.sqrt(12);
  }

  private buildFeedItem(raw: any): PortfolioFeedItem {
    const summary = raw.performance_summary || {};
    const symbolPerformance: any[] = raw.symbol_performance || [];
    const portfolioSym = symbolPerformance.find((e) => e.Ticker === 'Portfolio');
    const annualReturn =
      summary['Annualized Return']?.Portfolio ?? portfolioSym?.CAGR ?? 0;
    const volatility =
      summary['Annualized Std Dev']?.Portfolio ?? portfolioSym?.Volatility ?? 0;
    const maxDd = portfolioSym?.['Max Drawdown'] ?? 0;

    const holdings = symbolPerformance.filter((e) => e.Ticker !== 'Portfolio' && (e.Weight || 0) > 0);
    // The /portfolios list endpoint omits per-constituent rows from
    // symbol_performance, so the filter above can return 0 even when the
    // portfolio actually holds securities. Fall back to the top-level
    // security_count field that the same response carries (also used by
    // PortfolioModel and the legacy portfolio table).
    const holdingsCount = holdings.length || Number(raw.security_count) || 0;

    const topSorted = [...holdings].sort((a, b) => (b.Weight || 0) - (a.Weight || 0));
    const top3 = topSorted.slice(0, 3);
    const top3Sum = top3.reduce((a, b) => a + (b.Weight || 0), 0);
    const topName = top3[0]?.Ticker || '';

    const weights = this.buildTopWeights(topSorted);

    const headline = this.chooseHeadline(
      {
        annualReturn,
        volatility,
        maxDd,
        top3Sum,
      },
      weights.length,
    );

    const summaryText = this.buildSummary({
      name: raw.name || 'Portfolio',
      headline,
      annualReturn,
      volatility,
      maxDd,
      top3Sum,
      holdingsCount,
      topName,
    });

    const series = this.initialSeries(headline);

    return {
      id: raw.id,
      name: raw.name || 'Portfolio',
      when: this.formatWhen(raw.last_updated_at || raw.updated_at || raw.created_at),
      headline,
      summary: summaryText,
      series,
      weights,
      caption: this.CAPTIONS[headline.kind],
      risk: (volatility || 0) * 100,
      holdings: holdingsCount,
      topName,
      metricText: this.fmtLevel(headline),
      arrow: headline.dir === 'up' ? '▲' : '▼',
      chartColor: headline.kind === 'drawdown' ? this.CHART_COLOR.neg : this.CHART_COLOR[headline.tone],
      // The 'concentration' headline is rendered straight from the
      // synchronous weights data, so it never needs the async hydrate hop.
      isLoadingSeries: headline.kind !== 'concentration',
    };
  }

  private buildTopWeights(topSorted: any[]): HomeMiniWeight[] {
    if (!topSorted.length) return [];
    const top3 = topSorted.slice(0, 3).map((h) => ({ label: h.Ticker, weight: (h.Weight || 0) * 100 }));
    const rest = topSorted.slice(3);
    const otherSum = rest.reduce((a, b) => a + (b.Weight || 0), 0) * 100;
    if (otherSum > 0.05) top3.push({ label: 'Other', weight: otherSum });
    return top3;
  }

  // Pick the most newsworthy real signal from the portfolio's published
  // metrics. Priority order is fixed so the choice is deterministic and
  // explainable; we never invent a delta when no historical snapshot exists.
  private chooseHeadline(
    ctx: { annualReturn: number; volatility: number; maxDd: number; top3Sum: number },
    weightCount: number,
  ): Headline {
    const volPct = (ctx.volatility || 0) * 100;
    const ddPct = (ctx.maxDd || 0) * 100;
    const concPct = (ctx.top3Sum || 0) * 100;

    if (ddPct <= -15) return this.buildHeadline('drawdown', ctx);
    if (volPct >= 30) return this.buildHeadline('risk', ctx);
    if (concPct >= 60 && weightCount > 0) return this.buildHeadline('concentration', ctx);
    return this.buildHeadline('return', ctx);
  }

  private buildHeadline(
    kind: HomeMiniKind,
    ctx: { annualReturn: number; volatility: number; maxDd: number; top3Sum: number },
  ): Headline {
    const annPct = (ctx.annualReturn || 0) * 100;
    const volPct = (ctx.volatility || 0) * 100;
    const ddPct = (ctx.maxDd || 0) * 100;
    const concPct = (ctx.top3Sum || 0) * 100;

    if (kind === 'risk') {
      const elevated = volPct >= 30;
      return {
        kind: 'risk',
        dir: elevated ? 'down' : 'up',
        label: 'Volatility',
        level: volPct,
        tone: elevated ? 'warn' : volPct >= 18 ? 'info' : 'pos',
      };
    }

    if (kind === 'drawdown') {
      const deep = ddPct <= -15;
      return {
        kind: 'drawdown',
        dir: 'down',
        label: 'Max drawdown',
        level: ddPct,
        tone: deep ? 'neg' : ddPct <= -8 ? 'warn' : 'pos',
      };
    }

    if (kind === 'concentration') {
      const heavy = concPct >= 60;
      return {
        kind: 'concentration',
        dir: heavy ? 'down' : 'up',
        label: 'Top-3 concentration',
        level: concPct,
        tone: heavy ? 'warn' : 'info',
      };
    }

    return {
      kind: 'return',
      dir: annPct >= 0 ? 'up' : 'down',
      label: 'Annualised return',
      level: annPct,
      tone: annPct >= 0 ? 'pos' : 'neg',
    };
  }

  private buildSummary(ctx: {
    name: string;
    headline: Headline;
    annualReturn: number;
    volatility: number;
    maxDd: number;
    top3Sum: number;
    holdingsCount: number;
    topName: string;
  }): string {
    const name = `<span class="lnk">${ctx.name}</span>`;
    const h = ctx.headline;
    const annPct = (ctx.annualReturn || 0) * 100;
    const volPct = (ctx.volatility || 0) * 100;
    const ddPct = (ctx.maxDd || 0) * 100;
    const concPct = (ctx.top3Sum || 0) * 100;
    const volBand = volPct > 40 ? 'highly elevated' : volPct > 30 ? 'elevated' : volPct > 18 ? 'moderate' : 'contained';
    const holdings = ctx.holdingsCount === 1 ? '1 security' : `${ctx.holdingsCount} securities`;

    if (h.kind === 'return') {
      const absPct = Math.abs(annPct).toFixed(2);
      const verb = annPct >= 0 ? 'returned' : 'lost';
      const volPart = volPct > 0 ? ` Annualised volatility <b>${volPct.toFixed(1)}%</b> (${volBand}).` : '';
      return `${name} ${verb} <b>${absPct}%</b> annualised over the available history.${volPart} Currently holding ${holdings}.`;
    }
    if (h.kind === 'risk') {
      const retPart = annPct !== 0
        ? ` Annualised return <b>${annPct >= 0 ? '+' : ''}${annPct.toFixed(2)}%</b>.`
        : '';
      return `Annualised volatility for ${name} runs at <b>${volPct.toFixed(1)}%</b> (${volBand}).${retPart} Spread across ${holdings}.`;
    }
    if (h.kind === 'drawdown') {
      const ddLevel = Math.abs(ddPct).toFixed(1);
      const volPart = volPct > 0 ? ` Annualised volatility <b>${volPct.toFixed(1)}%</b> (${volBand}).` : '';
      return `Peak-to-trough drawdown for ${name} reached <b>-${ddLevel}%</b> over the available history.${volPart} ${holdings} held.`;
    }
    if (h.kind === 'concentration') {
      const topPart = ctx.topName ? ` Largest position is <b>${ctx.topName}</b>.` : '';
      return `Top-3 holdings represent <b>${concPct.toFixed(0)}%</b> of ${name}.${topPart} ${holdings} in total.`;
    }
    return '';
  }

  // Until populateSeries replaces the chart with real data we render an
  // empty series. The mini-chart component handles empty input gracefully —
  // better an empty box for ~one frame than a fabricated curve.
  private initialSeries(_h: Headline): number[] {
    return [];
  }

  private fmtLevel(h: Headline): string {
    if (h.kind === 'return') {
      const sign = h.level >= 0 ? '+' : '';
      return `${sign}${h.level.toFixed(2)}%`;
    }
    if (h.kind === 'risk') return `${h.level.toFixed(1)}%`;
    if (h.kind === 'drawdown') return `${h.level.toFixed(1)}%`;
    if (h.kind === 'concentration') return `${h.level.toFixed(0)}%`;
    return '';
  }

  private sampleSeries(values: number[], n: number): number[] {
    if (!values.length) return [];
    if (values.length <= n) return values.slice();
    const step = values.length / n;
    const out: number[] = [];
    for (let i = 0; i < n; i++) out.push(values[Math.floor(i * step)]);
    out.push(values[values.length - 1]);
    return out;
  }

  private mergeLists(resp: any): any[] {
    const buckets = ['private', 'shared_by_me', 'shared_with_me'];
    const out: any[] = [];
    const seen = new Set<number>();
    for (const key of buckets) {
      const list = Array.isArray(resp?.[key]) ? resp[key] : [];
      for (const item of list) {
        if (!item || !item.id || seen.has(item.id)) continue;
        seen.add(item.id);
        out.push(item);
      }
    }
    return out;
  }

  private sortByRecency(list: any[]): any[] {
    const toTime = (p: any) => {
      const raw = p?.last_updated_at || p?.updated_at || p?.created_at;
      if (!raw) return 0;
      const ts = Date.parse(typeof raw === 'string' && !raw.includes('T') ? raw.replace(' ', 'T') : raw);
      return isNaN(ts) ? 0 : ts;
    };
    return [...list].sort((a, b) => toTime(b) - toTime(a));
  }

  private computeGreeting(): string {
    const h = new Date().getHours();
    if (h < 5) return 'Working late';
    if (h < 12) return 'Good morning';
    if (h < 18) return 'Good afternoon';
    return 'Good evening';
  }

  private resolveUserName(user: any): string {
    if (!user) return '';
    if (user.first_name && user.first_name.trim()) return user.first_name.trim();
    if (user.email) return String(user.email).split('@')[0];
    return '';
  }

  private formatWhen(raw: string | undefined): string {
    if (!raw) return 'Recently';
    const ts = Date.parse(typeof raw === 'string' && !raw.includes('T') ? raw.replace(' ', 'T') : raw);
    if (isNaN(ts)) return 'Recently';
    const diffMs = Date.now() - ts;
    const day = 86_400_000;
    if (diffMs < day) return 'Today';
    if (diffMs < 2 * day) return 'Yesterday';
    if (diffMs < 7 * day) return `${Math.round(diffMs / day)} days ago`;
    if (diffMs < 30 * day) return `${Math.round(diffMs / (7 * day))} weeks ago`;
    return new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }
}
